/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createHotkeys } from 'shared/utils';
import { ClickableArea } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import CloseIcon from 'ud-ui/components/icon/close-icon';
import { liftToastLayer, releaseToastLayer } from 'ud-ui/components/toast-layer/toast-layer-host';

import Portal from '../portal/portal';
import { ignoreDirty, MODAL_CONTAINER_CLASS, ModalLayerContext } from './modal-layer-context';
import { getModalTop, MODAL_EDGE_RATIO } from './modal-position';
import { isTopModal, pushModal, removeModal } from './modal-stack';
import * as S from './styles';

export type UDModalSize = keyof typeof S.MODAL_WIDTHS;

export interface UDModalProps {
  children: React.ReactNode | React.ReactNode[];

  show: boolean;
  onClose?: () => void;
  /** Фиксированная ширина окна: `sm` 440px, `md` 560px (по умолчанию), `lg` 760px. */
  size?: UDModalSize;
  /** Ширина окна в пикселях. Переопределяет `size`, когда контенту нужна нестандартная ширина. */
  width?: number;
  /**
   * Высота окна в пикселях — окно не растёт и не сжимается от количества строк внутри.
   * `max-height: 90vh` в `ModalContainer` подрезает её на невысоких экранах. Контент
   * сам решает, что скроллить внутри этой высоты (см. `championship-team-players.tsx`),
   * поэтому при заданной `height` контейнер модалки перестаёт скроллиться целиком.
   */
  height?: number;
  /**
   * Содержимое может выступать за раму окна (`overflow: unset`). Не обход для списков: список внутри окна
   * кит и так рисует поверх (`useModalMenuPortal`), а `unset` ломает прокрутку `Modal.Body` с
   * закреплёнными шапкой и кнопками. Ставится только с комментарием-причиной рядом —
   * guard `src/shared/__tests__/modal-canon.test.ts`.
   */
  noOverflow?: boolean;

  disableEscapeKey?: boolean;
  disableScrollLock?: boolean;
  disableOverlayClick?: boolean;
}

export interface ModalFrameProps extends UDModalProps {
  /** Отдаётся содержимому через `useModalLayer()` — так форма сообщает окну о правках. */
  setDirty?: (dirty: boolean) => void;
  /**
   * Окно не закрывается сразу, а сначала спрашивает (`UDModal` с правками в форме). Если браузер
   * закрыл `<dialog>` сам (Chrome при повторном Escape), рама открывает его обратно и зовёт `onClose`.
   */
  holdsOpen?: () => boolean;
}

/**
 * Рама окна — нативный `<dialog>`, открытый `showModal()` (IMGM-1623). Браузер даёт верхний слой
 * без `z-index`, `inert` всего, что вне окна (фокус и клик туда не доходят), фокус в окне при
 * открытии и возврат фокуса на кнопку страницы после `close()`.
 *
 * Escape остаётся своим (`modal-stack.ts`): нативный `cancel` гасится, иначе браузер закрыл
 * бы окно мимо `disableEscapeKey` и мимо стека.
 *
 * Снаружи кита рама не используется: окно — `UDModal` (`modal.tsx`), он добавляет защиту формы
 * с правками. Раму напрямую берёт `Modal.Confirm` — ему защита не нужна, а через `UDModal`
 * получился бы круговой импорт (`UDModal` сам рисует `Modal.Confirm`).
 */
export const ModalFrame = (props: ModalFrameProps) => {
  const modalId = useMemo(() => Symbol('modal'), []);

  // Escape закрывает только верхнюю модалку. Обработчики всех открытых модалок
  // висят на одном `window`, поэтому «съесть» событие соседу нельзя — вместо
  // этого нижние отсеиваются по стеку (`modal-stack.ts`).
  //
  // Слушатель живёт в эффекте, а не в теле компонента: `createHotkeys` вешает
  // его на `window` сразу при вызове, и без `dispose` каждый рендер добавлял бы
  // ещё один, а закрытая модалка продолжала бы слушать Escape.
  //
  // Escape в раскрытом поле (`aria-expanded="true"`: открытый список react-select) сворачивает
  // только список — окно остаётся (WAI-ARIA combobox: Escape закрывает всплывающий список).
  // `defaultPrevented` для этого не годится: react-select гасит Escape и при закрытом списке.
  // Признак снимается на фазе перехвата — к всплытию до `window` react-select уже свернул список
  // и сбросил `aria-expanded`.
  //
  // Свой Escape окно гасит само (`preventDefault` на `window`): погашенный `keydown` не даёт
  // браузеру прислать `cancel`.
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  useEffect(() => {
    if (!props.show || props.disableEscapeKey) return;

    let collapsesPopup = false;
    const rememberExpanded = (event: KeyboardEvent) => {
      collapsesPopup = event.target instanceof Element && event.target.getAttribute('aria-expanded') === 'true';
    };
    window.addEventListener('keydown', rememberExpanded, true);

    pushModal(modalId);
    const hotkeys = createHotkeys([
      {
        combo: ['Escape'],
        when: () => isTopModal(modalId),
        handler: (event) => {
          event.preventDefault();
          if (collapsesPopup) return;
          onCloseRef.current?.();
        },
      },
    ]);

    return () => {
      window.removeEventListener('keydown', rememberExpanded, true);
      hotkeys.dispose();
      removeModal(modalId);
    };
  }, [modalId, props.disableEscapeKey, props.show]);

  // Блокировка скролла body: `<dialog>` её не делает.
  useEffect(() => {
    if (!props.show || props.disableScrollLock) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [props.disableScrollLock, props.show]);

  if (!props.show) {
    return null;
  }

  return <ModalDialog {...props} onCloseRef={onCloseRef} />;
};

interface ModalDialogProps extends ModalFrameProps {
  onCloseRef: React.MutableRefObject<(() => void) | undefined>;
}

/**
 * Сам `<dialog>`. `Portal` рендерит детей только со второго прохода, поэтому узел приходит
 * в состояние через `ref`, и эффект `showModal()` ждёт его (на этом умерла прежняя ловушка
 * Tab — журнал плана IMGM-1623, фаза 1).
 *
 * `Portal` — внутри этого компонента, а не снаружи: при закрытии React снимает эффекты
 * сверху вниз, и `Portal` в своей очистке вынимает контейнер из `body`. Будь он родителем,
 * `close()` пришёлся бы на отсоединённый `<dialog>` — браузер не вернул бы фокус на кнопку
 * страницы, а тосты ушли бы из DOM вместе с окном.
 */
function ModalDialog({ onCloseRef, ...props }: ModalDialogProps) {
  const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);

  const disableEscapeKeyRef = useRef(props.disableEscapeKey);
  disableEscapeKeyRef.current = props.disableEscapeKey;
  const holdsOpenRef = useRef(props.holdsOpen);
  holdsOpenRef.current = props.holdsOpen;

  useLayoutEffect(() => {
    if (!dialog) return;

    // Escape — свой (стек в `ModalFrame`), нативную отмену гасим.
    const preventNativeCancel = (event: Event) => event.preventDefault();
    // Chrome не даёт отменить `cancel` при повторном Escape без жеста пользователя — окно
    // закрывается само. Пока окно должно быть открыто, это закрытие ведём в `onClose`, а при
    // `disableEscapeKey` открываем окно обратно. Окно, которое сначала спрашивает (`holdsOpen`),
    // тоже открываем обратно: вопрос задаётся поверх него.
    const handleClose = () => {
      // Отложенное `close` от собственного `close()` при повторном монтировании (StrictMode).
      if (dialog.open) return;
      if (disableEscapeKeyRef.current) {
        dialog.showModal();
        return;
      }
      if (holdsOpenRef.current?.()) dialog.showModal();
      onCloseRef.current?.();
    };

    dialog.addEventListener('cancel', preventNativeCancel);
    dialog.addEventListener('close', handleClose);
    if (!dialog.open) dialog.showModal();
    liftToastLayer(dialog);

    return () => {
      dialog.removeEventListener('cancel', preventNativeCancel);
      dialog.removeEventListener('close', handleClose);
      releaseToastLayer(dialog);
      if (dialog.open) dialog.close();
    };
  }, [dialog, onCloseRef]);

  // Нажатие началось внутри окна (на поле, в тексте), а отпущено на затемнении — браузер шлёт `click`
  // общему предку, то есть самому `<dialog>`. Это не клик по затемнению: так закрывалось окно табло, когда
  // `mouseup` по полю приходился на заглушку раскрытого списка, и любое окно — при выделении текста
  // за край. Как в MUI Dialog: закрываем, только если и нажатие было на затемнении.
  const pressStartedInsideRef = useRef(false);
  const handleOverlayMouseDown = useCallback((event: React.MouseEvent<HTMLDialogElement>) => {
    pressStartedInsideRef.current = event.target !== event.currentTarget;
  }, []);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDialogElement>) => {
      const pressStartedInside = pressStartedInsideRef.current;
      pressStartedInsideRef.current = false;
      if (props.disableOverlayClick || pressStartedInside) return;

      // `<dialog>` растянут на весь экран, белое окно — внутри: клик по самому `<dialog>`
      // (и по его `::backdrop`) — это клик по затемнению.
      if (event.target === event.currentTarget) {
        onCloseRef.current?.();
      }
    },
    [onCloseRef, props.disableOverlayClick],
  );

  const setDirty = props.setDirty ?? ignoreDirty;
  const layer = useMemo(() => ({ dialog, setDirty }), [dialog, setDirty]);

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  useModalTop(container);

  return (
    <Portal wrapperId="portal_modal_container">
      <ModalLayerContext.Provider value={layer}>
        <S.Dialog ref={setDialog} onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
          <S.ModalContainer
            ref={setContainer}
            className={classNames(MODAL_CONTAINER_CLASS, 'px-6 pt-8.5 pb-10')}
            $width={props.width ?? S.MODAL_WIDTHS[props.size ?? 'md']}
            $height={props.height}
            style={props.height ? { overflow: 'hidden' } : props.noOverflow ? { overflow: 'unset' } : undefined}
          >
            <div className="close">
              <EmptyButton
                onClick={props.onClose}
                icon={
                  <ClickableArea horizontalExtend={10} verticalExtend={10}>
                    <CloseIcon />
                  </ClickableArea>
                }
              />
            </div>
            {props.children}
          </S.ModalContainer>
        </S.Dialog>
      </ModalLayerContext.Provider>
    </Portal>
  );
}

const MODAL_RESIZE_MS = 200;
const MODAL_RESIZING_ATTR = 'data-resizing';

/**
 * Верх окна: по центру при открытии, дальше закреплён (`modal-position.ts`). Флексом окно
 * больше не центрируется — флекс пересчитывал центр при любой смене высоты, и верх окна
 * прыгал на половину разницы (IMGM-1623).
 *
 * Смена высоты от содержимого (поле появилось / пропало) — плавная, 200 ms `ease-out`, без
 * анимации при `prefers-reduced-motion` (`design-system.md` §1 «Motion»). CSS `transition`
 * тут не срабатывает: у окна `height: auto` и до, и после, меняется только содержимое, а
 * переход ловит смену заданного значения. Поэтому — `element.animate()` от прежней высоты к
 * новой; верх сразу ставится под новую высоту, и растущее окно не уходит за край экрана.
 *
 * `top` и анимация пишутся в узел напрямую, без React-состояния: перерисовка формы ради
 * анимации не нужна. Высота — из `offsetHeight`: `getBoundingClientRect` во время `slideIn`
 * даёт ×0.95.
 */
function useModalTop(container: HTMLDivElement | null) {
  useLayoutEffect(() => {
    if (!container) return;

    let pinnedTop: number | undefined;
    let height: number | undefined;
    let resizing: Animation | undefined;
    let observer: ResizeObserver | undefined;

    const place = () => {
      // Кадры самой анимации — не смена содержимого; итог проверяется после неё.
      if (resizing) return;

      const nextHeight = container.offsetHeight;
      const viewportHeight = window.innerHeight;
      pinnedTop = getModalTop({
        viewportHeight,
        modalHeight: nextHeight,
        pinnedTop,
        edge: viewportHeight * MODAL_EDGE_RATIO,
      });
      container.style.top = `${pinnedTop}px`;

      if (height !== undefined && height !== nextHeight && canAnimate(container)) {
        // Анимация меняет высоту прямо в колбэке наблюдателя — без `unobserve` браузер
        // сообщает «ResizeObserver loop completed with undelivered notifications». После
        // анимации `observe` сам присылает свежий размер, и `place` сверяет итог.
        observer?.unobserve(container);
        // Пока высота догоняет содержимое, полосы прокрутки не мигают (`styles.ts`).
        container.setAttribute(MODAL_RESIZING_ATTR, '');
        resizing = container.animate([{ height: `${height}px` }, { height: `${nextHeight}px` }], {
          duration: MODAL_RESIZE_MS,
          easing: 'ease-out',
        });
        const done = () => {
          resizing = undefined;
          container.removeAttribute(MODAL_RESIZING_ATTR);
          if (observer) observer.observe(container);
          else place();
        };
        resizing.onfinish = done;
        resizing.oncancel = done;
      }
      height = nextHeight;
    };

    place();
    window.addEventListener('resize', place);
    observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(place);
    observer?.observe(container);

    return () => {
      window.removeEventListener('resize', place);
      observer?.disconnect();
      if (resizing) {
        resizing.onfinish = null;
        resizing.oncancel = null;
        resizing.cancel();
      }
    };
  }, [container]);
}

function canAnimate(element: HTMLElement) {
  return typeof element.animate === 'function' && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
